import React, { useState, memo, useCallback, useEffect } from "react";
import { ToDo, TripX } from "@/types/trip";
import useAuthStore from "@/stores/user/authStore";
import { Tabs, Tab } from "@heroui/tabs";
import useLodgeStore from "@/stores/lodge/lodgeStore";
import {
  addTodo as addTodoLib,
  toggleTodoComplete as toggleTodoCompleteLib,
  deleteTodo as deleteTodoLib,
} from "@/lib/lodge";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import useCurrencyStore from "@/stores/ui/currencyStore";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import { formatPrice } from "@/lib/general";

interface Location {
  id: string;
  name: string;
  address_components: {
    country: string;
  };
  budget: number;
}

export type Currency = "USD" | "EUR" | "GBP" | "JPY" | "AUD" | "CAD";

const currencySymbols: Record<Currency, string> = {
  USD: "$",
  EUR: "€",
  GBP: "£",
  JPY: "¥",
  AUD: "A$",
  CAD: "C$",
};

interface TodoInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onAdd: () => void;
  placeholderTextSize?: string;
}

const TodoInput = memo(
  ({
    value,
    onChange,
    onKeyDown,
    onAdd,
    placeholderTextSize,
  }: TodoInputProps) => {
    return (
      <div className="relative group">
        <div className="relative flex items-center rounded-lg p-1 2xl:p-2">
          <Input
            type="text"
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder="What needs to be done?"
            className={`flex-1 h-8 2xl:h-9 pl-3 pr-10 text-sm 2xl:text-base text-neutral-800 font-light placeholder:font-light bg-transparent placeholder-neutral-400 border-none focus:outline-none focus:ring-0 ${
              placeholderTextSize
                ? `placeholder:text-${placeholderTextSize} 2xl:placeholder:text-sm`
                : "placeholder:text-xs 2xl:placeholder:text-sm"
            }`}
          />
          <button
            onClick={onAdd}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 2xl:w-8 2xl:h-8 flex items-center justify-center text-white bg-indigo-700 rounded-full transition-all duration-300 shadow-sm"
            title="Add task"
          >
            <i className="fa-solid fa-plus text-xs 2xl:text-sm"></i>
          </button>
        </div>
      </div>
    );
  }
);

TodoInput.displayName = "TodoInput";

interface TodoItemProps {
  todo: ToDo;
  onToggle: (todoId: number) => void;
  onDelete: (todo: ToDo) => void;
}

const formatTodoTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  // Format time
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
  };
  const time = date.toLocaleTimeString(undefined, timeOptions);

  // Check if today
  if (date.toDateString() === now.toDateString()) {
    return `Today, ${time}`;
  }

  // Check if yesterday
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${time}`;
  }

  // Otherwise show date
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };
  const dateStr = date.toLocaleDateString(undefined, dateOptions);
  return `${dateStr}, ${time}`;
};

const TodoItem = memo(({ todo, onToggle, onDelete }: TodoItemProps) => (
  <div
    className={`group relative overflow-hidden transition-all duration-300 ease-in-out ${
      todo.completed ? "opacity-70" : ""
    }`}
  >
    <div className="relative flex items-center py-2 2xl:py-2 pl-3 pr-2 transition-all duration-300">
      <button
        onClick={() => onToggle(todo.id)}
        className={`shrink-0 relative w-3 h-3 2xl:w-4 2xl:h-4 rounded-full flex items-center justify-center cursor-pointer transition-all duration-300 ease-in-out ${
          todo.completed
            ? "bg-gradient-to-r from-indigo-600 to-purple-600"
            : "ring-2 ring-indigo-400 hover:ring-indigo-600"
        }`}
      ></button>
      <div className="flex-grow ml-3 flex flex-col">
        <span
          onClick={() => onToggle(todo.id)}
          className={`text-xs 2xl:text-sm font-light cursor-pointer ${
            todo.completed ? "text-neutral-500" : "text-neutral-800"
          }`}
        >
          {todo.text}
        </span>
        <span className="text-[10px] 2xl:text-xs text-neutral-400 mt-0.5">
          {formatTodoTime(todo.created_at)}
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete(todo);
        }}
        className="p-1 rounded-full text-neutral-400 hover:text-rose-500 hover:bg-rose-50 transition-all duration-300 opacity-0 group-hover:opacity-100"
        title="Delete task"
      >
        <XCircleIcon className="w-6 h-6 2xl:w-7 2xl:h-7" />
      </button>
    </div>
  </div>
));

TodoItem.displayName = "TodoItem";

export const ToDoList: React.FC<{
  tripId: string;
  placeholderTextSize?: string;
}> = ({ tripId, placeholderTextSize = "xs" }) => {
  const { addTodo, toggleTodoComplete, deleteTodo, updateTodoId, tripTodos } =
    useLodgeStore();
  const [inputValue, setInputValue] = useState("");
  const { toast } = useToast();

  const todos = tripTodos[tripId] || [];

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    },
    []
  );
  const handleAddTodo = useCallback(
    async (e?: React.KeyboardEvent<HTMLInputElement>) => {
      if (e && e.key !== "Enter") return;

      if (inputValue.trim() !== "") {
        const tempId = Math.floor(Math.random() * 1000000);
        const newTodo = {
          id: tempId,
          text: inputValue.trim(),
          completed: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setInputValue("");
        addTodo(tripId, newTodo);

        try {
          const response = await addTodoLib(tripId, inputValue.trim());
          // Update the todo with the real ID from the server
          updateTodoId(tripId, tempId, response.id);
        } catch (error) {
          // Remove the todo if the API call fails
          deleteTodo(tripId, tempId);
          toast({
            title: "Error adding todo",
            description: "There was an error adding the todo.",
            duration: 5000,
            variant: "destructive",
          });
        }
      }
    },
    [inputValue, tripId, toast, addTodo, updateTodoId, deleteTodo]
  );

  const handleToggleTodo = useCallback(
    async (todoId: number) => {
      toggleTodoComplete(tripId, todoId);

      try {
        await toggleTodoCompleteLib(tripId, todoId);
      } catch (error) {
        console.error("Failed to toggle todo:", error);
        // Revert the toggle if the API call fails
        toggleTodoComplete(tripId, todoId);
        toast({
          title: "Error updating todo",
          description: "There was an error updating the todo status.",
          duration: 5000,
          variant: "destructive",
        });
      }
    },
    [tripId, toast, toggleTodoComplete]
  );

  const handleDeleteTodo = useCallback(
    async (todo: ToDo) => {
      deleteTodo(tripId, todo.id);

      try {
        await deleteTodoLib(tripId, todo.id);
      } catch (error) {
        // Add the todo back if the API call fails
        addTodo(tripId, todo);
        console.error("Failed to delete todo:", error);
        toast({
          title: "Error deleting todo",
          description: "There was an error deleting the todo.",
          duration: 5000,
          variant: "destructive",
        });
      }
    },
    [tripId, toast, deleteTodo, addTodo]
  );

  return (
    <div className="flex-1 flex flex-col w-full h-full rounded-xl overflow-hidden border border-neutral-200">
      {/* Header */}
      <div className="bg-white rounded-b-md border-b border-neutral-200 shadow-sm">
        <TodoInput
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleAddTodo}
          onAdd={() => handleAddTodo()}
          placeholderTextSize={placeholderTextSize}
        />
      </div>

      {/* List Section */}
      <div className="flex-1 overflow-y-auto no-scrollbar">
        {!todos.length ? (
          <div className="flex flex-col items-center justify-center h-full text-neutral-400 rounded-lg p-6 2xl:p-8 space-y-2 2xl:space-y-3">
            <div className="w-16 h-16 2xl:w-20 2xl:h-20 rounded-full bg-indigo-50 flex items-center justify-center">
              <ClipboardIcon className="w-8 h-8 2xl:w-10 2xl:h-10 text-indigo-400" />
            </div>
            <div className="text-center">
              <p className="text-sm 2xl:text-base font-medium text-indigo-900">
                No tasks yet
              </p>
              <p className="text-xs 2xl:text-sm text-neutral-500 font-light mt-1">
                Add your first task to get started
              </p>
            </div>
          </div>
        ) : (
          <div>
            {/* Todo items */}
            {todos.map((todo: ToDo) => (
              <TodoItem
                key={todo.id}
                todo={todo}
                onToggle={handleToggleTodo}
                onDelete={handleDeleteTodo}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

interface CurrencyCostProps {
  currentBudget: number;
}

const CurrencyCost: React.FC<CurrencyCostProps> = ({ currentBudget }) => {
  const roundUpToWholeNumber = (num: number) => Math.ceil(num);
  const { selectedCurrency, isCurrencyLoading } = useCurrencyStore();

  return (
    <span
      className={`font-medium text-indigo-800 transition-all duration-300 ${
        isCurrencyLoading ? "opacity-60 blur-[1px]" : ""
      }`}
    >
      {currencySymbols[selectedCurrency.code as Currency]}
      {roundUpToWholeNumber(currentBudget)}
    </span>
  );
};

export const BudgetComp: React.FC<{ trip: TripX | null }> = ({ trip }) => {
  const { selectedCurrency, isCurrencyLoading } = useCurrencyStore();

  const getFlagImage = (countryName: string) => {
    const countryKey = countryName.toLowerCase().replace(/\s/g, "");
    return `/assets/worldFlags/${countryKey}.png`;
  };

  const destinationLocations = trip?.locations.slice(1) || [];

  const totalBudgets = destinationLocations.reduce(
    (acc, location) => ({
      total: acc.total + (location.budget.total || 0),
      accommodation: acc.accommodation + (location.budget.accommodation || 0),
      transport: acc.transport + (location.budget.transport || 0),
      activities: acc.activities + (location.budget.activities || 0),
    }),
    { total: 0, accommodation: 0, transport: 0, activities: 0 }
  );

  // Price component with loading effect
  const PriceDisplay = ({
    amount,
    compact = false,
  }: {
    amount: number;
    compact?: boolean;
  }) => (
    <span
      className={`font-${
        compact ? "semibold" : "medium"
      } text-indigo-800 transition-all duration-300 ${
        isCurrencyLoading ? "opacity-60 blur-[1px]" : ""
      }`}
    >
      {formatPrice(selectedCurrency.code, amount, compact)}
    </span>
  );

  return (
    <div className="flex-1 flex flex-col w-full h-full rounded-xl overflow-hidden border border-neutral-200">
      {/* Header */}
      <div className="p-2 2xl:p-3 bg-white rounded-b-md border-b border-neutral-200 shadow-sm">
        <div className="flex items-center">
          <div className="flex items-center space-x-1.5 2xl:space-x-2">
            <span className="text-xs 2xl:text-sm text-neutral-600 font-light">
              Total Cost:
            </span>
            <span
              className={`text-md 2xl:text-lg font-medium text-indigo-800 transition-all duration-300 ${
                isCurrencyLoading ? "opacity-60 blur-[1px]" : ""
              }`}
            >
              {formatPrice(selectedCurrency.code, totalBudgets.total)}
            </span>
          </div>
          <div className="ml-auto text-xs 2xl:text-sm text-neutral-400">
            {selectedCurrency.code}
          </div>
        </div>
      </div>

      {/* Destinations List */}
      <div className="flex-grow overflow-y-auto no-scrollbar py-3 px-2 space-y-3 2xl:space-y-2">
        {!destinationLocations.length ? (
          <div className="flex flex-col items-center justify-center h-full text-neutral-400 bg-white/70 backdrop-blur-sm rounded-lg p-6 2xl:p-8 space-y-3 2xl:space-y-4">
            <div className="w-16 h-16 2xl:w-20 2xl:h-20 rounded-full bg-indigo-50 flex items-center justify-center">
              <i className="fa-solid fa-map-location-dot text-indigo-400 text-2xl 2xl:text-3xl"></i>
            </div>
            <div className="text-center">
              <p className="text-sm 2xl:text-sm font-medium text-indigo-900">
                No destinations yet
              </p>
              <p className="text-xs 2xl:text-xs text-neutral-500 mt-1 2xl:mt-2">
                Add destinations to see your budget
              </p>
            </div>
          </div>
        ) : (
          destinationLocations.map((location) => (
            <div
              key={location.id}
              className="group flex items-center justify-between bg-white rounded-lg transition-all duration-300 p-2 2xl:p-3"
            >
              <div className="flex items-center space-x-3 2xl:space-x-4">
                <div className="rounded-full overflow-hidden flex items-center justify-center">
                  <img
                    src={getFlagImage(location.address_components.country)}
                    alt={`${location.address_components.country} flag`}
                    className="w-5 h-5 2xl:w-6 2xl:h-6 object-cover"
                  />
                </div>
                <div>
                  <span className="text-sm 2xl:text-sm font-medium text-neutral-800">
                    {location.name}
                  </span>
                  <div className="text-xs 2xl:text-xs text-neutral-500 font-light">
                    {location.address_components.country}
                  </div>
                </div>
              </div>
              <PriceDisplay amount={location.budget.total} compact={true} />
            </div>
          ))
        )}
      </div>

      {/* Budget Categories */}
      <div className="mt-auto p-2 2xl:p-3 bg-white">
        <div className="grid grid-cols-3 gap-2 2xl:gap-3">
          {[
            {
              label: "Stays",
              value: totalBudgets.accommodation,
              icon: "fa-solid fa-bed",
            },
            {
              label: "Transport",
              value: totalBudgets.transport,
              icon: "fa-solid fa-car-side",
            },
            {
              label: "Activities",
              value: totalBudgets.activities,
              icon: "fa-solid fa-person-hiking",
            },
          ].map((category) => (
            <div
              key={category.label}
              className="px-2 2xl:px-3 py-1 2xl:py-2 rounded-lg border border-neutral-100 bg-neutral-50"
            >
              <span className="text-sm 2xl:text-base font-semibold text-indigo-800">
                {formatPrice(selectedCurrency.code, category.value, true)}
              </span>
              <h2 className="text-xs 2xl:text-sm font-light text-neutral-600 mt-0.5 2xl:mt-1">
                {category.label}
              </h2>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface ProfileComponentProps {
  isLoading: boolean;
  trips: TripX[];
}

const ProfileComponent: React.FC<ProfileComponentProps> = ({
  isLoading,
  trips,
}) => {
  const { user, profile_image } = useAuthStore();
  const defaultAvatar = "user/defaultUser.png";

  const tripsCount = trips ? trips.length : 0;

  return (
    <div className="flex flex-1 flex-col w-full h-full border-[1px] border-neutral-100 rounded-md">
      {/* Header */}
      <div className="flex flex-row p-4 2xl:p-6 items-center">
        {/* Circular user photo */}
        <div className="w-14 h-14 2xl:w-16 2xl:h-16 rounded-full overflow-hidden mr-2 2xl:mr-3">
          <img
            src={profile_image ?? defaultAvatar}
            alt={`${user?.first_name}'s profile`}
            className="w-full h-full object-cover"
          />
        </div>

        {/* User name and city */}
        <div className="flex flex-col justify-center">
          <h2 className="text-base 2xl:text-lg font-semibold text-neutral-800">
            {user?.first_name}
          </h2>
          <p className="text-xs 2xl:text-sm text-neutral-600">
            Sydney, Australia
          </p>
        </div>

        <button className="ml-auto w-10 h-10 2xl:w-12 2xl:h-12 p-3 rounded-full border border-neutral-200 bg-neutral-100/50 flex items-center justify-center hover:bg-neutral-200/75 transition ease-in-out duration-500">
          <i className="fa-regular fa-pen-to-square 2xl:text-lg"></i>
        </button>
      </div>

      {/* Scrollable Content Container */}
      <div className="flex-grow  p-4">
        {/* Add your scrollable content here */}
      </div>

      {/* Fixed Bottom Stats Boxes */}
      <div className="mt-auto p-4">
        <div className="flex flex-row space-x-3">
          {[
            { label: "Friends", value: 150, icon: "fa-solid fa-users" },
            {
              label: "Trips",
              value: isLoading ? "..." : tripsCount,
              icon: "fa-solid fa-plane",
            },
            {
              label: "Assists",
              value: 23,
              icon: "fa-solid fa-handshake-angle",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex-1 p-2 bg-neutral-100 text-xs text-neutral-700 flex flex-col items-center justify-center rounded-lg"
            >
              <i className={`${stat.icon} text-indigo-700 text-xl mb-2`}></i>
              <p className="text-lg font-semibold text-neutral-800">
                {stat.value}
              </p>
              <h2 className="mt-1">{stat.label}</h2>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
